import React from 'react'
import { connect } from 'react-redux'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { PaywallTemplate } from '@lmc/ui-kit'
import { IconType } from '@lmc/ui-kit'
import { colours } from '@lmc/ui-kit/src/config/theme'
import { HomeSource, ResidentStatus } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from '../../../locale'
import useResidentsList from 'hooks/useResidentsList'
import styled from 'styled-components'
import { constants } from '@lmc/common'

interface StateProps {
  subscribeTo: () => void
  getSubscriptions: () => void
  subscriptionCancelled?: boolean
  role?: string
  source?: HomeSource
  backgroundColor?: string
  backgroundImage?: string
  title: string
  subTitle: string
  bodyText: string
  icon:
    | IconType.CarePlansHexagon
    | IconType.DocsHexagon
    | IconType.EmarHexagon
    | IconType.FamilyHexagon
    | IconType.GroupsHexagon
    | IconType.ProHexagon
    | IconType.HandoversHexagon
    | IconType.ConsentHexagon
  videoUrl: string
  articles: Array<{
    title: string
    subtitle: string
    icon: string
    id: string
    link: string
    iconBackgroundColour: string
    iconColour: string
  }>
  leftModuleCard: React.ReactElement
  rightModuleCard: React.ReactElement
  findOutMoreURL: string
  customLinkText?: string
  testId?: string
}

export const PaywallTemplateWrapperComponent: React.FC<StateProps> = ({
  subscribeTo,
  getSubscriptions,
  subscriptionCancelled,
  role,
  source,
  backgroundColor = colours.status.popout,
  backgroundImage,
  title,
  subTitle,
  bodyText,
  icon,
  videoUrl,
  articles,
  leftModuleCard,
  rightModuleCard,
  findOutMoreURL,
  customLinkText = 'Read more',
  testId,
}) => {
  const { t } = useTranslation(NAMESPACE.GENERAL)
  const { residents, isLoading } = useResidentsList()

  React.useEffect(() => {
    getSubscriptions()
  }, [getSubscriptions])

  const isSalesLed = source === HomeSource.salesLed
  const ctaDisabled =
    role === 'senior-carer' || subscriptionCancelled || isLoading

  const handleContactSupport = () => {
    if (window.Intercom) {
      window.Intercom('showNewMessage')
    }
  }

  const cancelledSubscriptionTooltip = (
    <div>
      {t('CANCELLED_SUBSCRIPTION_TOOLTIP_TEXT')}
      <SupportLink onClick={handleContactSupport}>
        {t('CANCELLED_SUBSCRIPTION_CONTACT_SUPPORT')}
      </SupportLink>
      {t('CANCELLED_SUBSCRIPTION_TOOLTIP_SUFFIX')}
    </div>
  )

  let disabledText
  if (subscriptionCancelled) {
    disabledText = cancelledSubscriptionTooltip
  } else if (role === 'senior-carer') {
    disabledText = 'Note: Only admins can start a free trial.'
  }

  const countableResidents =
    residents?.filter(
      (resident) =>
        resident.status &&
        [
          ResidentStatus.active,
          ResidentStatus.inactive,
          ResidentStatus.hospitalised,
        ].includes(resident.status),
    ) || []
  const hasTooManyResidents =
    countableResidents.length > constants.MAX_FREE_RESIDENTS

  const shouldShowContactSales = hasTooManyResidents || isSalesLed

  const ctaText = shouldShowContactSales ? 'Contact us' : 'Start Free Trial'
  const handleCtaClick = shouldShowContactSales
    ? () => window.open(constants.HUBSPOT_DISCOVERY_CALL_URL, '_blank')
    : subscribeTo

  return (
    <PaywallTemplate
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      title={title}
      subTitle={subTitle}
      bodyText={bodyText}
      cta={ctaText}
      onClick={handleCtaClick}
      icon={icon}
      videoUrl={videoUrl}
      articles={articles}
      leftModuleCard={leftModuleCard}
      rightModuleCard={rightModuleCard}
      ctaDisabled={ctaDisabled}
      disabledText={disabledText}
      findOutMoreURL={findOutMoreURL}
      customLinkText={customLinkText}
      testId={testId}
    />
  )
}

const mapStateToProps = (state: any) => ({
  subscriptionCancelled: Selectors.isSubscriptionCancelled(state, 'pro'),
  role: state.global?.user?.role,
  source: state.global?.home?.source,
})

const mapDispatchToProps = (dispatch: any) => ({
  subscribeTo: () => dispatch(Operations.openProPlanCheckout({})),
  getSubscriptions: () => dispatch(Operations.getSubscriptions()),
})

export const PaywallTemplateWrapper = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PaywallTemplateWrapperComponent)

const SupportLink = styled.span`
  color: ${({ theme }) => theme.color.status.primary};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.7;
  }
`
